import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Search, Filter, Loader2 } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Layout } from '../components/Layout';
import { MenuCard } from '../components/MenuCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MenuPage = () => {
  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [categoriesRes, itemsRes] = await Promise.all([
          axios.get(`${API}/menu/categories`),
          axios.get(`${API}/menu/items`),
        ]);
        setCategories(categoriesRes.data);
        setMenuItems(itemsRes.data);
      } catch (error) {
        // Menu fetch failed; UI shows "Aucun plat trouvé"
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const filteredItems = menuItems.filter((item) => {
    const matchesCategory = !selectedCategory || item.category_id === selectedCategory;
    const matchesSearch =
      !searchQuery ||
      item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const getCategoryName = (categoryId) => {
    const category = categories.find((c) => c.category_id === categoryId);
    return category?.name || '';
  };

  const groupedItems = filteredItems.reduce((acc, item) => {
    const categoryName = getCategoryName(item.category_id);
    if (!acc[categoryName]) {
      acc[categoryName] = [];
    }
    acc[categoryName].push(item);
    return acc;
  }, {});

  return (
    <Layout>
      {/* Header */}
      <section className="bg-muted py-12 md:py-16" data-testid="menu-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <span className="text-primary font-accent text-xl">Notre Carte</span>
            <h1 className="text-3xl md:text-5xl font-serif font-bold mt-2">
              L'Île Gourmande
            </h1>
            <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
              Découvrez notre sélection de plats mauriciens et français, 
              préparés sur commande avec des produits frais.
            </p>
          </div>

          {/* Search & Filters */}
          <div className="flex flex-col md:flex-row gap-4 items-center justify-center">
            <div className="relative w-full md:w-96">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Rechercher un plat..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 rounded-full"
                data-testid="search-input"
              />
            </div>
          </div>

          {/* Category filters */}
          <div className="flex flex-wrap gap-2 justify-center mt-6">
            <Button
              variant={selectedCategory === null ? 'default' : 'outline'}
              className="rounded-full"
              onClick={() => setSelectedCategory(null)}
              data-testid="filter-all"
            >
              Tous
            </Button>
            {categories.map((category) => (
              <Button
                key={category.category_id}
                variant={selectedCategory === category.category_id ? 'default' : 'outline'}
                className="rounded-full"
                onClick={() => setSelectedCategory(category.category_id)}
                data-testid={`filter-${category.category_id}`}
              >
                {category.name}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Menu Items */}
      <section className="py-12 md:py-16" data-testid="menu-items-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-muted-foreground">Aucun plat trouvé</p>
            </div>
          ) : (
            <div className="space-y-12">
              {Object.entries(groupedItems).map(([categoryName, items]) => (
                <div key={categoryName} data-testid={`category-section-${categoryName}`}>
                  <div className="flex items-center space-x-4 mb-6">
                    <h2 className="text-2xl md:text-3xl font-serif font-semibold">
                      {categoryName}
                    </h2>
                    <Badge variant="secondary" className="rounded-full">
                      {items.length} plats
                    </Badge>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {items.map((item) => (
                      <MenuCard key={item.item_id} item={item} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default MenuPage;
